import React from 'react';
import {
    TextInput,
    required
} from 'react-admin';
import FieldDescription from './FieldDescription';
import FieldDivider from './FieldDivider';
import SelectFromListInput from './SelectFromListInput';
import {validateStoreUrl, validateEmailList} from '../utils/dataFunctions';

/**
 * A component for displaying a Shopify Store Integration Form in a Title Edit view
 *
 * @param record
 * @param type
 * @return {JSX.Element}
 * @constructor
 */
const StoreIntegrationFormShopify = ({record, type = 'create'}) => {

    const validateRequired = [required()];
    const validateUrl = [required(), validateStoreUrl];
    const validateEmails = [validateEmailList];

    return (
        <>
            {type === 'create' &&
                <>
                    <FieldDescription
                        instructions={true} marginBottom={true}
                        text={`To integrate with Shopify, an application must be created in the 
                                Shopify store's Dev Dashboard and installed in the store. That application 
                                will create fields specific to that store. Once created, add its data 
                                below.`}
                    />
                    <FieldDescription
                        instructions={true} marginBottom={true}
                        text={`Once this Title is saved with its integration, Shopify will send 
                                new orders from that store to Parrot Communications.`}
                    />
                </>
            }
            {type === 'edit' &&
                <>
                    <FieldDescription
                        instructions={true} marginBottom={true}
                        text={`The fields below are derived from an application installed on Shopify. These 
                        fields are unlikely to need editing unless the application was changed or if you 
                        need to change email notifications or billing reference.`}
                    />
                </>
            }
            <FieldDivider type="break" margin="bottom"/>

            <FieldDescription
                description={true}
                text={`The Shopify Store's URL without https protocol. Example: store-749237498237498737.myshopify.com`}/>
            <TextInput label="Store URL" source="store_url"
                       fullWidth={true} isRequired={true} validate={validateUrl}/>
            <FieldDescription
                description={true}
                text={`The Store Application Client ID is a long alphanumeric string to identify the Parrot application.`}/>
            <TextInput label="Store Application Client ID" source="store_app_client_id"
                       fullWidth={true} isRequired={true} validate={validateRequired}/>
            <FieldDescription
                description={true}
                text={`The Store Application Secret is a confidential key to authenticate the Parrot application.`}/>
            <TextInput label="Store Application Secret" source="store_app_secret"
                       fullWidth={true}
                       isRequired={type === 'create'}
                       validate={type === 'create' ? validateRequired : undefined}
                       placeholder={type === 'edit' ? 'Secret exists — enter a new value to change' : ''}/>

            {type === 'create' &&
                <FieldDescription
                    description={true}
                    text={`Select a Billing Reference associated with this Title's Shopify store.`}/>
            }
            {type === 'edit' &&
                <TextInput label="Current Billing Reference" source="store_bill_ref_desc"
                           className="input-inline input-220" disabled/>
            }
            <SelectFromListInput
                inAdminForm={true}
                source="store_bill_ref_id_select"
                resourceName="BillingReference"
                listLabel={(type === 'create')
                    ? 'Select a Billing Reference for this Shopify store'
                    : 'If desired, select a new Billing Reference:'}
                itemPlural="Billing References"
                listFilters={{
                    active: true,
                    params: {
                        filter: {
                            filter_org_id: record.org_id
                        },
                    }
                }}
                returnType="string"
                className="input-inline input-400"
                isRequired={true}
            />

            <FieldDivider type="break" margin="bottom"/>
            <FieldDivider type="break" margin="bottom"/>
            {type === 'create' &&
                <FieldDescription
                    instructions={true} marginBottom={true}
                    text={`Enter one or more valid email addresses that would 
                                       receive notifications regarding orders. Separate each email with
                                       a comma.`}
                />
            }
            {type === 'edit' &&
                <FieldDescription
                    instructions={true} marginBottom={true}
                    text={`Optional: additional email addresses to receive notifications regarding 
                    orders (the inventory team address is always notified). Separate each email 
                    with a comma.`}
                />
            }
            <FieldDescription
                description={true}
                text={`Example: email1@example.com,email2@example.com`}
            />
            <FieldDivider type="break"/>
            <TextInput label="Email Addresses (optional)" source="store_email_addresses_str"
                       fullWidth={true} isRequired={false} validate={validateEmails} multiline/>

        </>
    );
};

export default StoreIntegrationFormShopify;
